import React, { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, ZoomControl } from "react-leaflet";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import {
  Search,
  MapPin,
  User,
  Briefcase,
  Filter,
  X,
  GraduationCap,
  Building,
  Globe,
  CheckCircle,
  ExternalLink,
  GithubIcon,
  Linkedin,
  Twitter,
  LayoutGrid,
  Map as MapIcon
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Constants for map configuration
const AHMEDABAD_CENTER = [23.0225, 72.5714];
const DEFAULT_ZOOM = 11;
const MIN_ZOOM = 4;
const MAX_ZOOM = 18;

// Fix default marker icon issues
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Create custom marker icon
const createCustomMarker = (alumni) => {
  const count = Array.isArray(alumni) ? alumni.length : 1;
  const singleAlumnus = Array.isArray(alumni) ? alumni[0] : alumni;
  
  const baseClasses = "relative transform transition-transform duration-300 hover:scale-110";
  const markerSize = count > 1 ? "w-16 h-16" : "w-12 h-12";
  
  return L.divIcon({
    className: "alumni-marker",
    html: `
      <div class="${baseClasses} ${markerSize}">
        ${count > 1 ? `
          <div class="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full opacity-20 animate-pulse"></div>
          <div class="w-full h-full rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-purple-500">
            <div class="text-center">
              <span class="text-purple-600 font-bold">${count}</span>
              <span class="text-xs block text-purple-600">Alumni</span>
            </div>
          </div>
        ` : `
          <div class="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full opacity-20 animate-pulse"></div>
          <div class="w-full h-full rounded-full bg-white shadow-lg overflow-hidden border-2 border-blue-500">
            ${singleAlumnus.personalInfo?.avatar ? `
              <img src="${singleAlumnus.personalInfo.avatar}" class="w-full h-full object-cover" alt="Alumni" />
            ` : `
              <div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                <span class="text-blue-500 font-bold">${singleAlumnus.firstName?.[0] || 'A'}</span>
              </div>
            `}
          </div>
          ${singleAlumnus.isVerified ? `
            <div class="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full shadow-lg">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
          ` : ''}
        `}
      </div>
    `,
    iconSize: count > 1 ? [64, 64] : [48, 48],
    iconAnchor: count > 1 ? [32, 32] : [24, 24],
    popupAnchor: [0, count > 1 ? -32 : -24]
  });
};

// Enhanced SearchBar component
const SearchBar = ({ onSearch, onLocationSelect, onFilter }) => {
  const [query, setQuery] = useState("");
  const [locations, setLocations] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  const searchLocation = debounce(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setLocations([]);
      return;
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();
      
      const formattedLocations = data.slice(0, 5).map(item => ({
        id: item.place_id,
        name: item.display_name,
        coordinates: [parseFloat(item.lat), parseFloat(item.lon)],
        type: item.type
      }));
      
      setLocations(formattedLocations);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Location search failed:", error);
    }
  }, 300);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    onSearch(value);
    searchLocation(value);
  };

  const handleClear = () => {
    setQuery("");
    onSearch("");
    setLocations([]);
    setShowSuggestions(false);
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={handleInputChange}
            placeholder="Search alumni, companies, or locations..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
          {query && (
            <button
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
        </div>
        <button
          onClick={onFilter}
          className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <Filter className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      <AnimatePresence>
        {showSuggestions && locations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50"
          >
            {locations.map((location) => (
              <button
                key={location.id}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-0"
                onClick={() => {
                  onLocationSelect(location);
                  setQuery(location.name);
                  setShowSuggestions(false);
                }}
              >
                <MapPin className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {location.name}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {location.type}
                  </p>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// FilterPanel Component
const FilterPanel = ({ isOpen, onClose, filters, onFilterChange }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0, x: "100%" }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: "100%" }}
        className="fixed right-0 top-0 h-full w-80 bg-white shadow-xl p-6 z-[9999]"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Filters</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="text-sm font-medium text-gray-700">Department</label>
            <select
              className="mt-1 w-full p-2 border border-gray-200 rounded-lg"
              value={filters.department}
              onChange={(e) => onFilterChange("department", e.target.value)}
            >
              <option value="">All Departments</option>
              <option value="Computer Engineering">Computer Engineering</option>
              <option value="Information Technology">Information Technology</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Graduation Year</label>
            <select
              className="mt-1 w-full p-2 border border-gray-200 rounded-lg"
              value={filters.graduationYear}
              onChange={(e) => onFilterChange("graduationYear", e.target.value)}
            >
              <option value="">All Years</option>
              {[2024, 2025].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Employment Status</label>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.employed}
                  onChange={(e) => onFilterChange("employed", e.target.checked)}
                  className="rounded text-blue-500"
                />
                <span className="text-sm">Currently Employed</span>
              </label>
            </div>
          </div>

         
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gray-50 border-t">
          <button
            onClick={() => onFilterChange("reset")}
            className="w-full py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Reset Filters
          </button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Map Content Component
const MapContent = ({ alumni, center, zoom, onMarkerClick }) => {
  const map = useMap();
  
  const fitMarkerBounds = () => {
    if (alumni.length > 0) {
      const bounds = L.latLngBounds(alumni.map(group => {
        const [lng, lat] = group[0].personalInfo.currentLocation.coordinates;
        return [lat, lng];
      }));
      
      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 13,
        minZoom: 9,
        animate: true,
        duration: 0.8
      });
    } else {
      // Reset to Ahmedabad view
      map.setView(AHMEDABAD_CENTER, DEFAULT_ZOOM, {
        animate: true,
        duration: 0.8
      });
    }
  };

  // Effect to handle bounds fitting when alumni changes
  useEffect(() => {
    if (alumni.length > 0) {
      fitMarkerBounds();
    }
  }, [alumni]);

  // Effect to handle center and zoom changes
  useEffect(() => {
    if (center && Array.isArray(center)) {
      map.flyTo(center, zoom || map.getZoom(), {
        duration: 1,
        easeLinearity: 0.25
      });
    }
  }, [center, zoom, map]);

  return (
    <>
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; OpenStreetMap contributors'
      />
      <ZoomControl position="bottomright" />
      {alumni.map((group) => {
        const [lng, lat] = group[0].personalInfo.currentLocation.coordinates;
        return (
          <Marker
            key={`${lat}-${lng}`}
            position={[lat, lng]}
            icon={createCustomMarker(group)}
            eventHandlers={{
              click: () => onMarkerClick(group)
            }}
          >
            <Popup>
              <AlumniPopup alumni={group} />
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

// Main Map Component
const MapComponent = ({ alumni, center, zoom, onMarkerClick }) => {
  return (
    <div className="h-full w-full relative">
      <MapContainer
        center={center || AHMEDABAD_CENTER}
        zoom={zoom || DEFAULT_ZOOM}
        className="h-full w-full"
        zoomControl={false}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
      >
        <MapContent 
          alumni={alumni} 
          center={center} 
          zoom={zoom} 
          onMarkerClick={onMarkerClick}
        />
      </MapContainer>
    </div>
  );
};
// Alumni Popup Component
const AlumniPopup = ({ alumni, onClose }) => {
  const navigate = useNavigate();

  return (
    <div className="min-w-[300px] max-w-[400px]">
      {alumni.map((alumnus) => (
        <div
          key={alumnus._id}
          className="p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 cursor-pointer"
          onClick={() => navigate(`/alumni/${alumnus._id}`)}
        >
          <div className="flex items-start gap-3">
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white shadow-md">
                {alumnus.personalInfo?.avatar ? (
                  <img
                    src={alumnus.personalInfo.avatar}
                    alt={`${alumnus.firstName} ${alumnus.lastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                    <span className="text-blue-500 font-semibold">
                      {alumnus.firstName?.[0] || 'A'}
                    </span>
                  </div>
                )}
              </div>
              {alumnus.isVerified && (
                <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full shadow-md">
                  <CheckCircle className="h-3 w-3" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-medium text-gray-900 truncate">
                  {alumnus.firstName} {alumnus.lastName}
                </h3>
                <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </div>

              <div className="mt-1 space-y-1">
                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <GraduationCap className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">
                    {alumnus.academicInfo?.department} ({alumnus.graduationYear})
                  </span>
                </p>

                {alumnus.professionalInfo?.experiences?.find(e => e.isCurrent) && (
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Briefcase className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                      {alumnus.professionalInfo.experiences.find(e => e.isCurrent).title}
                      {" at "}
                      {alumnus.professionalInfo.experiences.find(e => e.isCurrent).company}
                    </span>
                  </p>
                )}

                <p className="text-sm text-gray-600 flex items-center gap-1">
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">
                    {alumnus.personalInfo?.currentLocation?.address}
                  </span>
                </p>
              </div>

              <div className="mt-3 flex items-center gap-2">
                {alumnus.socialLinks?.linkedin && (
                  <a
                    href={alumnus.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <Linkedin className="h-4 w-4" />
                  </a>
                )}
                {alumnus.socialLinks?.github && (
                  <a
                    href={alumnus.socialLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
                  >
                    <GithubIcon className="h-4 w-4" />
                  </a>
                )}
                {alumnus.socialLinks?.twitter && (
                  <a
                    href={alumnus.socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-blue-400 transition-colors"
                  >
                    <Twitter className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
const AlumniCard = ({ alumnus }) => {
  const navigate = useNavigate();
  
  if (!alumnus) return null;

  const currentExperience = alumnus.professionalInfo?.experiences?.find(e => e.isCurrent);
  
  return (
    <div
      onClick={() => navigate(`/alumni/${alumnus._id}`)}
      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-200"
    >
      <div className="p-6">
        {/* Header with Avatar and Name */}
        <div className="flex items-start gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow-md">
              {alumnus.personalInfo?.avatar ? (
                <img
                  src={alumnus.personalInfo.avatar}
                  alt={`${alumnus.firstName || ''} ${alumnus.lastName || ''}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
                  <span className="text-xl font-semibold text-blue-500">
                    {(alumnus.firstName?.[0] || 'A').toUpperCase()}
                  </span>
                </div>
              )}
            </div>
            {alumnus.isVerified && (
              <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full shadow-md">
                <CheckCircle className="h-4 w-4" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {[alumnus.firstName, alumnus.lastName].filter(Boolean).join(' ')}
              </h3>
              <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
            </div>

            {/* Academic Info */}
            {(alumnus.academicInfo?.department || alumnus.graduationYear) && (
              <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                <GraduationCap className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {[
                    alumnus.academicInfo?.department,
                    alumnus.graduationYear && `(${alumnus.graduationYear})`
                  ].filter(Boolean).join(' ')}
                </span>
              </p>
            )}

            {/* Current Position */}
            {currentExperience && (
              <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                <Briefcase className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {[
                    currentExperience.title,
                    currentExperience.company && `at ${currentExperience.company}`
                  ].filter(Boolean).join(' ')}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Location */}
        {alumnus.personalInfo?.currentLocation?.address && (
          <div className="mt-4 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm text-gray-600 truncate">
              {alumnus.personalInfo.currentLocation.address}
            </span>
          </div>
        )}

        {/* Social Links */}
        {(alumnus.socialLinks?.linkedin || alumnus.socialLinks?.github || alumnus.socialLinks?.twitter) && (
          <div className="mt-4 flex items-center gap-2">
            {alumnus.socialLinks?.linkedin && (
              <a
                href={alumnus.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            )}
            {alumnus.socialLinks?.github && (
              <a
                href={alumnus.socialLinks.github}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
              >
                <GithubIcon className="h-4 w-4" />
              </a>
            )}
            {alumnus.socialLinks?.twitter && (
              <a
                href={alumnus.socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-blue-400 transition-colors"
              >
                <Twitter className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
// Main AlumniMap Component
const AlumniMap = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState("map");
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState(AHMEDABAD_CENTER);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [filters, setFilters] = useState({
    department: "",
    graduationYear: "",
    employed: false,
    verified: false
  });

  // Fetch alumni data
  useEffect(() => {
    const fetchAlumni = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/directory`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch alumni data");
        const data = await response.json();
        setAlumni(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAlumni();
  }, []);

  // Handle location selection
  const handleLocationSelect = (location) => {
    setMapCenter(location.coordinates);
    setMapZoom(13);
  };

  // Handle search with improved behavior
  const handleSearch = debounce((value) => {
    setSearchQuery(value);
    
    if (!value.trim()) {
      setMapCenter(AHMEDABAD_CENTER);
      setMapZoom(DEFAULT_ZOOM);
      return;
    }

    const filtered = alumni.filter(alumnus => {
      return value.toLowerCase().split(" ").every(term =>
        `${alumnus.firstName} ${alumnus.lastName} ${alumnus.personalInfo?.currentLocation?.address || ""} ${
          alumnus.professionalInfo?.experiences?.find(e => e.isCurrent)?.company || ""
        }`.toLowerCase().includes(term)
      );
    });

    if (filtered.length > 0) {
      const locations = filtered.map(a => a.personalInfo?.currentLocation?.coordinates).filter(Boolean);
      
      if (locations.length === 1) {
        const [lng, lat] = locations[0];
        setMapCenter([lat, lng]);
        setMapZoom(13);
      } else if (locations.length > 1) {
        // Calculate bounds for multiple results
        const lats = locations.map(([lng, lat]) => lat);
        const lngs = locations.map(([lng, lat]) => lng);
        
        const centerLat = (Math.max(...lats) + Math.min(...lats)) / 2;
        const centerLng = (Math.max(...lngs) + Math.min(...lngs)) / 2;
        setMapCenter([centerLat, centerLng]);

        // Calculate appropriate zoom level
        const latDiff = Math.max(...lats) - Math.min(...lats);
        const lngDiff = Math.max(...lngs) - Math.min(...lngs);
        const maxDiff = Math.max(latDiff, lngDiff);
        const zoom = Math.min(Math.max(Math.floor(8 / maxDiff), 9), 13);
        setMapZoom(zoom);
      }
    } else {
      setMapCenter(AHMEDABAD_CENTER);
      setMapZoom(DEFAULT_ZOOM);
    }
  }, 300);

// Continuing the AlumniMap component...

  // Filter and group alumni by location
  const groupedAlumni = useMemo(() => {
    const filtered = alumni.filter(alumnus => {
      const matchesSearch = searchQuery.toLowerCase().split(" ").every(term =>
        `${alumnus.firstName} ${alumnus.lastName} ${alumnus.personalInfo?.currentLocation?.address || ""} ${
          alumnus.professionalInfo?.experiences?.find(e => e.isCurrent)?.company || ""
        }`.toLowerCase().includes(term)
      );

      const matchesFilters = (
        (!filters.department || alumnus.academicInfo?.department === filters.department) &&
        (!filters.graduationYear || alumnus.graduationYear.toString() === filters.graduationYear) &&
        (!filters.employed || alumnus.professionalInfo?.currentlyEmployed) &&
        (!filters.verified || alumnus.isVerified)
      );

      return matchesSearch && matchesFilters;
    });

    // Group by location
    const groups = new Map();
    filtered.forEach(alumnus => {
      if (!alumnus.personalInfo?.currentLocation?.coordinates) return;
      
      const [lng, lat] = alumnus.personalInfo.currentLocation.coordinates;
      const key = `${lat},${lng}`;
      
      if (!groups.has(key)) {
        groups.set(key, [alumnus]);
      } else {
        groups.get(key).push(alumnus);
      }
    });

    return Array.from(groups.values());
  }, [alumni, searchQuery, filters]);

  const handleFilterChange = (key, value) => {
    if (key === "reset") {
      setFilters({
        department: "",
        graduationYear: "",
        employed: false,
        verified: false
      });
      setMapCenter(AHMEDABAD_CENTER);
      setMapZoom(DEFAULT_ZOOM);
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading alumni network...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-red-500 mb-4">
            <X className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-center mb-2">Error Loading Data</h3>
          <p className="text-gray-600 text-center">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-2 rounded-lg">
                <Globe className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Alumni Network</h1>
                <p className="text-sm text-gray-500">{alumni.length} Alumni Worldwide</p>
              </div>
            </div>

            <div className="flex-1 max-w-2xl">
              <SearchBar 
                onSearch={handleSearch} 
                onLocationSelect={handleLocationSelect}
                onFilter={() => setFilterOpen(true)}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode(viewMode === "map" ? "grid" : "map")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors tooltip"
                title={viewMode === "map" ? "Switch to Grid View" : "Switch to Map View"}
              >
                {viewMode === "map" ? (
                  <LayoutGrid className="h-5 w-5 text-gray-600" />
                ) : (
                  <MapIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative overflow-hidden">
        {viewMode === "map" ? (
          <MapComponent
            alumni={groupedAlumni}
            center={mapCenter}
            zoom={mapZoom}
            onMarkerClick={setSelectedMarker}
          />
        ) : (
          <div className="max-w-7xl mx-auto p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedAlumni.flat().map((alumnus) => (
                <AlumniCard key={alumnus._id} alumnus={alumnus} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Stats Bar */}
      <div className="bg-white border-t border-gray-200 py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-around">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{alumni.length}</div>
            <div className="text-sm text-gray-500">Total Alumni</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {new Set(alumni.map(a => a.personalInfo?.currentLocation?.address?.split(',').pop()?.trim())).size}
            </div>
            <div className="text-sm text-gray-500">Cities</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">
              {new Set(alumni.map(a => 
                a.professionalInfo?.experiences?.find(e => e.isCurrent)?.company
              )).size}
            </div>
            <div className="text-sm text-gray-500">Companies</div>
          </div>
        
        </div>
      </div>
    </div>
  );
};

export default AlumniMap;